import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import DataPill from "@components/pills/DataPill";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import { getApiErrorDisplay } from "@services/api/apiError";
import { analyticsApiServices } from "@services/apis/Analytics";
import {
  GetDailyTradeVolumeBrlData,
  GetDailyTradeVolumeBrlReponse,
} from "@services/models/Analytics/GetDailyTradeVolumeBrl";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import PayInPayOutChart from "./components/PayInPayOutChart";

const Analytics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<GetDailyTradeVolumeBrlData[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const constantFunctions = new ConstantFunctions();

  // Calculate totals from data
  const totalPayIn = useMemo(() => {
    return data.reduce((sum, item) => {
      const value =
        typeof item.payIn === "number" ? item.payIn : parseFloat(item.payIn);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }, [data]);

  const totalPayOut = useMemo(() => {
    return data.reduce((sum, item) => {
      const value =
        typeof item.payOut === "number" ? item.payOut : parseFloat(item.payOut);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }, [data]);

  const fetchDailyTradeVolume = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const response: GetDailyTradeVolumeBrlReponse =
        await analyticsApiServices.getDailyTradeVolumeBRL();
      setData(response.data.dailyData);
    } catch (err: any) {
      setError(getApiErrorDisplay(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDailyTradeVolume();
  }, []);

  // Column definitions for ag-grid
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Date",
        field: "date",
        flex: 1,
        minWidth: 120,
        maxWidth: 150,
        valueFormatter: (params) =>
          constantFunctions.dateFormatInDDMMYY(params.value),
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Pay-In",
        field: "payIn",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "MDR",
        field: "payInMDR",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Credited",
        field: "credited",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          const formattedValue = isNaN(value) ? "0.00" : value.toFixed(2);
          return <DataPill label={formattedValue} tone="green" />;
        },
      },
      {
        headerName: "Pay-Out",
        field: "payOut",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "MDR",
        field: "payOutMDR",
        flex: 0.8,
        minWidth: 100,
        maxWidth: 130,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Debited",
        field: "debited",
        flex: 1.2,
        minWidth: 120,
        maxWidth: 160,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          const formattedValue = isNaN(value) ? "0.00" : value.toFixed(2);
          return <DataPill label={formattedValue} tone="red" />;
        },
      },
      {
        headerName: "Funds Flow",
        field: "fundsFlow",
        flex: 1.2,
        minWidth: 130,
        maxWidth: 170,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Net Funds Flow",
        field: "netFundsFlow",
        flex: 1.2,
        minWidth: 140,
        maxWidth: 180,
        valueFormatter: (params) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          return isNaN(value) ? "0.00" : value.toFixed(2);
        },
        headerClass: "ag-header-cell-custom",
        cellClass: "ag-cell-custom",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: any) => {
          const value =
            typeof params.value === "number"
              ? params.value
              : parseFloat(params.value);
          const formattedValue = isNaN(value) ? "0.00" : value.toFixed(2);
          return (
            <DataPill
              label={formattedValue}
              tone={params.value < 0 ? "red" : "green"}
            />
          );
        },
      },
    ],
    [constantFunctions]
  );

  return (
    <>
      <div className="h-[72px] border-b border-border-subtle bg-bg-page-body flex flex-row sticky top-0 bg-white z-10 pl-[20px] w-full">
        <div className="flex items-center justify-between w-full pr-[20px]">
          <p className="flex text-text-base text-[14px] font-semibold items-center">
            Analytics
          </p>
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-row items-center gap-2 px-3 py-2 bg-bg-button-secondary border border-border-button-secondary rounded-lg">
              <p className="text-[12px] font-semibold text-text-base">
                Total Pay-In: ${totalPayIn.toFixed(2)}
              </p>
              <div className="h-[12px] border border-border-button-primary"></div>
              <p className="text-[12px] font-semibold text-text-base">
                Total Pay-Out: ${totalPayOut.toFixed(2)}
              </p>
            </div>
            <p className="text-[12px] text-text-subtle">(30 days)</p>
          </div>
        </div>
      </div>
      <div
        className="flex py-[20px] px-[12px]"
        style={{ height: "calc(50vh - 50px)" }}
      >
        {loading && data?.length === 0 ? (
          <AgGridSkeleton
            columnDefs={columnDefs}
            style={{ height: "100%", width: "100%" }}
          />
        ) : (
          <AgGridReactTable
            ref={gridRef}
            rowData={data}
            columnDefs={columnDefs}
            error={error}
          />
        )}
      </div>

      {/* Chart Section */}
      <div className="px-[12px] pb-[20px]">
        {loading && data?.length === 0 ? (
          <div className="h-[300px] border border-border-subtle rounded-lg bg-bg-base flex items-center justify-center">
            <p className="text-text-base">Loading chart...</p>
          </div>
        ) : (
          <PayInPayOutChart data={data} />
        )}
      </div>
    </>
  );
};

export default Analytics;
